<template>

  <KakaoChannelInvite 
    ref="kakaoInvite"
    badge="🎰"
    title="다음 이벤트도 놓치지 마세요!"
    message="채널 추가하면<br/><strong>새로운 룰렛·이벤트 소식</strong>을 알려드려요!"
  />

  <div class="container">
    <!-- 로딩 -->
    <div v-if="pageLoading" class="page-status">
      <div class="status-card">
        <i class="ph-bold ph-spinner spinning"></i>
        <p>룰렛을 준비하는 중...</p>
      </div>
    </div>

    <!-- 에러 -->
    <div v-else-if="pageError" class="page-status">
      <div class="status-card error">
        <i class="ph-fill ph-x-circle"></i>
        <h2>{{ pageError }}</h2>
        <p>{{ isAgentMode ? '로그인 후 다시 시도해주세요' : '링크를 다시 확인해주세요' }}</p>
      </div>
    </div>

    <!-- 만료 -->
    <div v-else-if="linkData.status === 'expired'" class="page-status">
      <div class="status-card">
        <i class="ph-fill ph-clock-countdown"></i>
        <h2>만료된 룰렛입니다</h2>
        <p>유효기간이 지났습니다</p>
      </div>
    </div>

    <!-- 모든 기회 소진 -->
    <div v-else-if="linkData.status === 'completed'" class="page-status">
      <div class="status-card">
        <i class="ph-fill ph-check-circle"></i>
        <h2>{{ isAgentMode ? '보유 티켓을 모두 사용했습니다' : '모든 뽑기를 완료했습니다!' }}</h2>
        <p>{{ isAgentMode ? '새 티켓을 받으면 다시 도전하세요 🎟️' : '참여해 주셔서 감사합니다 🎉' }}</p>
        <!-- 당첨 내역 -->
        <div class="history-summary" v-if="winHistory.length > 0">
          <h4>🎊 당첨 내역</h4>
          <div v-for="(h, i) in winHistory" :key="i" class="history-item win">
            <img v-if="h.imageUrl" :src="h.imageUrl" class="history-img" />
            <span>{{ h.name }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 본인인증 전 (고객 모드만) -->
    <div v-else-if="!verified && !isAgentMode" class="page-status">
      <div class="status-card verify">
        <div class="verify-header">
          <span class="verify-emoji">🎰</span>
          <h2>행운의 뽑기</h2>
          <p><strong>{{ linkData.sender_name }}</strong>님이 보낸 룰렛</p>
          <p class="verify-chances">🎟️ {{ remainingSpins }}회 뽑기 기회!</p>
        </div>

        <div class="verify-form">
          <label>전화번호를 입력하면 시작됩니다</label>
          <input 
            type="tel" 
            v-model="phone"
            placeholder="010-1234-5678"
            @input="formatPhoneInput"
            maxlength="13"
          />
          <button 
            class="verify-btn"
            :disabled="!canVerify"
            @click="handleVerify"
          >
            시작하기
          </button>
        </div>
      </div>
    </div>

    <!-- 룰렛 메인 -->
    <template v-else>
      <div class="roulette-container">
        <h2 class="roulette-title">행운의 뽑기</h2>
        
        <div class="roulette-wrapper">
          <div class="dial-bg dial-a"></div>
          <div class="dial-bg dial-b"></div>
          <div class="wheel-pointer">▼</div>
          
          <Roulette 
            ref="wheel"
            :key="spinKey"
            :items="wheelItems"
            :wheel-result-index="wheelResultIndex"
            :counter-clockwise="false"
            :display-border="false"
            :display-indicator="false"
            :display-shadow="false"
            :size="wheelSize"
            :duration="7"
            :result-variation="60"
            :easing="'cubic-bezier(0, 0.9, 0.05, 1)'"
            @wheel-start="onWheelStart"
            @wheel-end="onWheelEnd"
          />
        </div>
        
        <div class="extra">
          <span class="chance-badge">🎟️ 기회 x {{ remainingSpins }}</span>
          <button 
            class="spin-btn"
            :class="{ disabled: isSpinning || remainingSpins <= 0 }"
            :disabled="isSpinning || remainingSpins <= 0"
            @click="launchWheel"
          >
            <b>뽑기</b> {{ isSpinning ? '중...' : '도전!' }}
          </button>
        </div>
      </div>

      <!-- 결과 모달 -->
      <transition name="modal">
        <div class="modal-overlay" v-if="showResultModal" @click.self="closeResultModal">
          <div class="result-modal" :class="{ winner: result?.isWinner }">
            <div class="result-content">
              <div class="result-emoji">
                {{ result?.isWinner ? '🎊' : '😅' }}
              </div>
              <h2 class="result-title">
                {{ result?.isWinner ? '축하해요!' : '아쉬워요!' }}
              </h2>
              
              <div class="result-prize">
                <div class="result-prize-icon" :style="{ background: result?.bgColor }">
                  <img v-if="result?.imageUrl" :src="result.imageUrl" class="prize-img" :alt="result.name" />
                  <span v-else>{{ result?.icon }}</span>
                </div>
                <h3 class="result-prize-name">{{ result?.name }}</h3>
                <p class="result-prize-desc">{{ result?.description }}</p>
              </div>

              <div class="result-message">
                <p v-if="result?.isWinner">
                  🎉 {{ result?.goods_code ? '기프티콘이 발송되었습니다!' : '기프티콘이 곧 발송될 예정이에요!' }}
                </p>
                <p v-else>
                  다음엔 꼭 당첨되길 바랄게요!
                  <span v-if="remainingSpins > 0">
                    <br>아직 {{ remainingSpins }}번 남았어요 💪
                  </span>
                </p>
              </div>
            </div>

            <div class="result-actions">
              <button class="result-btn confirm" @click="closeResultModal">
                {{ remainingSpins > 0 ? '다시 도전' : '확인' }}
              </button>
            </div>
          </div>
        </div>
      </transition>

      <!-- 토스트 -->
      <transition name="toast">
        <div class="toast" v-if="showToastMsg" :class="toastType">
          <span class="toast-icon">{{ toastType === 'success' ? '✓' : 'ℹ' }}</span>
          <span>{{ toastMessage }}</span>
        </div>
      </transition>
    </template>
  </div>
</template>

<script>
import { ref, computed, onMounted, inject, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { Roulette } from 'vue3-roulette';
import KakaoChannelInvite from '@/components/dialogs/KakaoChannelInvite.vue'

export default {
  name: 'RoulettePage',
  components: { Roulette, KakaoChannelInvite},
  setup() {
    const route = useRoute();
    const token = route.params.token;
    const sql_query = inject('sql_query', null);
    const getMbid = inject('getMbid', null);
    const purchaseGifticon = inject('purchaseGifticon', null);
    const getGiftShowGoodsDetail = inject('getGiftShowGoodsDetail', null);

    // setup() 안에서
    const kakaoInvite = ref(null)

    // 카톡 채널 추천
    setTimeout(() => {
      kakaoInvite.value?.show()
    }, 2000) // 결과 본 뒤 2초 후

    // 모드 판별: 토큰 있으면 고객모드, 없으면 설계사(본인)모드
    const isAgentMode = !token;

    // 페이지 상태
    const pageLoading = ref(true);
    const pageError = ref('');
    const linkData = ref({});
    const verified = ref(false);
    const phone = ref('');

    // 룰렛 상태
    const wheel = ref(null);
    const spinKey = ref(0);
    const isSpinning = ref(false);
    const result = ref(null);
    const showResultModal = ref(false);
    const remainingSpins = ref(0);
    const winHistory = ref([]);

    const wheelResultIndex = ref({ value: null });

    // 토스트
    const showToastMsg = ref(false);
    const toastMessage = ref('');
    const toastType = ref('success');

    // 룰렛 사이즈 — 마운트 시 1회만 결정, 이후 절대 변경하지 않음
    // vue3-roulette는 :size prop이 바뀌면 내부 재렌더링 + 애니메이션 트리거됨
    const wheelSize = ref(300);

    // 경품 데이터
    const prizes = ref([
      { 
        name: '바나나우유', icon: '🍌',
        goodsCode: 'G00004291198',
        description: 'CU 바나나 우유',
        imageUrl: null,
        probability: 10, bgColor: '#FFF8DC', isWinner: true, value: 1,
        goods_code: 'G00004291198'
      },
      { 
        name: '초코우유', icon: '🍫',
        goodsCode: 'G00004291196',
        description: 'CU 허쉬 초코우유',
        imageUrl: null,
        probability: 10, bgColor: '#FFFACD', isWinner: true, value: 2,
        goods_code: 'G00004291196'
      },
      { 
        name: '신라면', icon: '🍜',
        goodsCode: 'G00004261253',
        description: 'CU 신라면 소컵',
        imageUrl: null,
        probability: 10, bgColor: '#FFF8DC', isWinner: true, value: 3,
        goods_code: 'G00004261253'
      },
      { 
        name: '육개장', icon: '🍲',
        goodsCode: 'G00004261270',
        description: 'CU 육개장 사발면',
        imageUrl: null,
        probability: 10, bgColor: '#FFFACD', isWinner: true, value: 4,
        goods_code: 'G00004261270'
      },
      { 
        name: '포카칩', icon: '🥔',
        goodsCode: 'G00004291210',
        description: '포카칩',
        imageUrl: null,
        probability: 5, bgColor: '#FFF8DC', isWinner: true, value: 5,
        goods_code: 'G00004291210'
      },
      { 
        name: '스벅 라떼', icon: '☕',
        goodsCode: 'G00004781012',
        description: '스타벅스 바닐라 아이스 라떼',
        imageUrl: null,
        probability: 5, bgColor: '#FFFACD', isWinner: true, value: 6,
        goods_code: 'G00004781012'
      },
      { 
        name: 'BBQ 치킨', icon: '🍗',
        goodsCode: 'G00004661005',
        description: 'BBQ 황금올리브치킨 + 콜라 1.25L',
        imageUrl: null,
        probability: 1, bgColor: '#FFE4B5', isWinner: true, value: 7,
        goods_code: 'G00004661005'
      },
      { 
        name: '꽝', icon: '🎃',
        goodsCode: null,
        description: '다음 기회에!',
        imageUrl: null,
        probability: 49, bgColor: '#f0f0f0', isWinner: false, value: 0,
        goods_code: ''
      },
    ]);

    // vue3-roulette 아이템
    const wheelItems = computed(() => {
      return prizes.value.map((prize, index) => {
        const iconHtml = prize.imageUrl 
          ? `<img src="${prize.imageUrl}" style="width:12%;margin-top:5%;height:auto;aspect-ratio:1/1;object-fit:contain;border-radius:6px;" alt="${prize.name}" />`
          : `<span style="font-size:1em;margin-top:5%;">${prize.icon}</span>`;
        
        return {
          id: index,
          name: prize.name,
          htmlContent: `
            <div style="display:flex;flex-direction:column;align-items:center;gap:2px;">
              ${iconHtml}
              <span style="font-size:0.2em;font-weight:800;color:#333;text-align:center;line-height:1.2;max-width:70px;word-break:keep-all;">${prize.name}</span>
            </div>
          `,
          textColor: '#333',
          background: prize.bgColor
        };
      });
    });

    // ========== 링크 데이터 로드 (고객 모드) ==========
    async function fetchLinkData() {
      try {
        const res = await sql_query(`
          SELECT rl.*, m.name as sender_name
          FROM roulette_links rl
          LEFT JOIN member m ON rl.mb_id = m.mb_id
          WHERE rl.token = '${token}'
        `);

        if (!res || res.length === 0) {
          pageError.value = '룰렛을 찾을 수 없습니다';
          return;
        }

        const link = res[0];

        // 만료 확인
        if (new Date(link.expires_at) < new Date()) {
          link.status = 'expired';
        }

        // 모든 횟수 소진
        if (link.used_count >= link.ticket_count && link.status !== 'expired') {
          link.status = 'completed';
        }

        linkData.value = link;
        remainingSpins.value = Math.max(0, link.ticket_count - link.used_count);

        // 당첨 내역 조회
        if (link.status === 'completed') {
          const history = await sql_query(`
            SELECT prize_name, is_winner, prize_detail
            FROM roulette_history
            WHERE coupon_code = '${token}' AND is_winner = 1
            ORDER BY created_at DESC
          `);
          if (history?.length > 0) {
            winHistory.value = history.map(h => {
              const p = prizes.value.find(pr => pr.name === h.prize_name);
              return {
                name: h.prize_name,
                imageUrl: p?.imageUrl || ''
              };
            });
          }
        }

      } catch (e) {
        console.error('링크 조회 실패:', e);
        pageError.value = '룰렛 정보를 불러올 수 없습니다';
      } finally {
        pageLoading.value = false;
      }
    }

    // ========== 설계사 모드 로드 ==========
    async function fetchAgentData() {
      try {
        const mbId = getMbid ? getMbid() : null;
        if (!mbId) {
          pageError.value = '로그인이 필요합니다';
          pageLoading.value = false;
          return;
        }

        // 본인 티켓 + 전화번호 조회
        const tickets = await sql_query(`
          SELECT SUM(rt.ticket_count - rt.used_count) as remaining
          FROM roulette_tickets rt
          WHERE rt.mb_id = '${mbId}' 
          AND rt.status = 'active' 
          AND (rt.expired_at IS NULL OR rt.expired_at > NOW())
          AND rt.ticket_count > rt.used_count
        `);

        const agentInfo = await sql_query(`
          SELECT phone, name FROM member WHERE mb_id = '${mbId}'
        `);

        const remaining = parseInt(tickets?.[0]?.remaining) || 0;
        remainingSpins.value = remaining;

        // 설계사 모드는 인증 불필요, 바로 룰렛
        linkData.value = { 
          status: remaining > 0 ? 'pending' : 'completed', 
          mb_id: mbId,
          agent_phone: agentInfo?.[0]?.phone || '',
          agent_name: agentInfo?.[0]?.name || ''
        };
        verified.value = true;

        if (remaining <= 0) {
          linkData.value.status = 'completed';
        }

      } catch (e) {
        console.error('티켓 조회 실패:', e);
        pageError.value = '티켓 정보를 불러올 수 없습니다';
      } finally {
        pageLoading.value = false;
      }
    }

    // ========== 본인 인증 ==========
    function formatPhoneInput() {
      let numbers = phone.value.replace(/[^0-9]/g, '');
      if (numbers.length <= 3) phone.value = numbers;
      else if (numbers.length <= 7) phone.value = numbers.slice(0, 3) + '-' + numbers.slice(3);
      else phone.value = numbers.slice(0, 3) + '-' + numbers.slice(3, 7) + '-' + numbers.slice(7, 11);
    }

    const canVerify = computed(() => {
      return phone.value.replace(/-/g, '').length === 11;
    });

    function handleVerify() {
      const cleanPhone = phone.value.replace(/-/g, '');
      if (linkData.value.receiver_phone !== cleanPhone) {
        alert('본인 확인에 실패했습니다');
        return;
      }
      verified.value = true;
    }

    // ========== 룰렛 로직 ==========
    function getRandomPrizeIndex() {
      const random = Math.random() * 100;
      let cumulative = 0;
      for (let i = 0; i < prizes.value.length; i++) {
        cumulative += prizes.value[i].probability;
        if (random <= cumulative) return i;
      }
      return prizes.value.length - 1;
    }

    function launchWheel() {
      if (isSpinning.value || remainingSpins.value <= 0) return;

      const winnerIndex = getRandomPrizeIndex();
      wheelResultIndex.value = { value: winnerIndex };
      spinKey.value++;

      nextTick(() => {
        setTimeout(() => {
          if (wheel.value) wheel.value.launchWheel();
        }, 100);
      });
    }

    function onWheelStart() {
      isSpinning.value = true;
      if (navigator.vibrate) navigator.vibrate(50);
    }

    function onWheelEnd(item) {
      isSpinning.value = false;

      let selectedPrize = null;
      if (wheelResultIndex.value?.value !== null && wheelResultIndex.value?.value !== undefined) {
        selectedPrize = prizes.value[wheelResultIndex.value.value];
      }
      if (!selectedPrize && item) {
        selectedPrize = typeof item.id !== 'undefined' 
          ? prizes.value[item.id] 
          : prizes.value.find(p => p.name === item.name);
      }
      if (!selectedPrize) selectedPrize = prizes.value[prizes.value.length - 1];

      result.value = selectedPrize;
      remainingSpins.value--;
      saveResult(selectedPrize);

      setTimeout(() => {
        showResultModal.value = true;
        if (navigator.vibrate) {
          navigator.vibrate(selectedPrize.isWinner ? [100, 50, 100] : [200]);
        }
      }, 300);
    }

    // 결과 저장
    async function saveResult(prize) {
      if (!sql_query) return;

      try {
        const prizeDetail = JSON.stringify({
          icon: prize.icon,
          description: prize.description,
          bgColor: prize.bgColor,
          imageUrl: prize.imageUrl || '',
          goodsCode: prize.goodsCode || ''
        }).replace(/'/g, "\\'");

        if (isAgentMode) {
          // 설계사 모드: roulette_history 기록 + 본인 티켓 차감
          const mbId = getMbid ? getMbid() : '';
          await sql_query(`
            INSERT INTO roulette_history 
            (mb_id, ip_address, prize_name, prize_value, is_winner, prize_detail, coupon_status, created_at) 
            VALUES 
            ('${mbId}', 'agent', '${prize.name}', ${prize.value}, ${prize.isWinner ? 1 : 0}, '${prizeDetail}', '${prize.isWinner ? 'pending' : 'used'}', NOW())
          `);

          // 티켓 차감
          await sql_query(`
            UPDATE roulette_tickets 
            SET used_count = used_count + 1,
                status = CASE WHEN used_count + 1 >= ticket_count THEN 'used' ELSE status END,
                updated_at = NOW()
            WHERE mb_id = '${mbId}' 
            AND status = 'active' 
            AND (expired_at IS NULL OR expired_at > NOW())
            AND ticket_count > used_count
            LIMIT 1
          `);
        } else {
          // 고객 모드: roulette_history + roulette_links used_count 증가
          await sql_query(`
            INSERT INTO roulette_history 
            (mb_id, ip_address, prize_name, prize_value, is_winner, prize_detail, coupon_code, coupon_status, created_at) 
            VALUES 
            ('${linkData.value.mb_id}', '${linkData.value.receiver_phone}', '${prize.name}', ${prize.value}, ${prize.isWinner ? 1 : 0}, '${prizeDetail}', '${token}', '${prize.isWinner ? 'pending' : 'used'}', NOW())
          `);

          await sql_query(`
            UPDATE roulette_links 
            SET used_count = used_count + 1,
                status = CASE WHEN used_count + 1 >= ticket_count THEN 'completed' ELSE status END
            WHERE token = '${token}'
          `);
        }

        // 당첨이면 기프티콘 발송 + 기록
        if (prize.isWinner) {
          winHistory.value.push({ name: prize.name, imageUrl: prize.imageUrl });

          // 기프티콘 API 발송
          if (purchaseGifticon && prize.goods_code) {
            try {
              const recipientPhone = isAgentMode
                ? (linkData.value.agent_phone || '').replace(/-/g, '')
                : (linkData.value.receiver_phone || phone.value.replace(/-/g, ''));

              const sendResult = await purchaseGifticon({
                mb_id: linkData.value.mb_id || (getMbid ? getMbid() : ''),
                goods_code: prize.goods_code,
                goods_name: prize.name,
                brand_name: '',
                goods_img: prize.imageUrl || '',
                sale_price: 0,
                discount_price: 0,
                recipient_type: isAgentMode ? 'self' : 'customer',
                recipient_name: isAgentMode ? (linkData.value.agent_name || '') : (linkData.value.receiver_name || ''),
                recipient_phone: recipientPhone,
                customer_idx: null,
                mms_msg: `🎉 [룰렛 당첨] ${prize.name} - ${prize.description}`
              });

              if (sendResult.success) {
                // 발송 성공 시 coupon_status 업데이트
                const historyUpdateField = isAgentMode ? `mb_id = '${getMbid ? getMbid() : ''}'` : `coupon_code = '${token}'`;
                await sql_query(`
                  UPDATE roulette_history 
                  SET coupon_status = 'sent'
                  WHERE ${historyUpdateField}
                  AND prize_name = '${prize.name}'
                  ORDER BY created_at DESC
                  LIMIT 1
                `);

                // customer_history 기록 (고객 모드일 때)
                if (!isAgentMode && linkData.value.receiver_phone) {
                  try {
                    const mbId = linkData.value.mb_id || '';
                    const cleanPhone = (linkData.value.receiver_phone || '').replace(/-/g, '');
                    const custRes = await sql_query(`
                      SELECT idx FROM customer 
                      WHERE mb_id = '${mbId}' 
                      AND REPLACE(phone, '-', '') = '${cleanPhone}'
                      LIMIT 1
                    `);
                    if (custRes && custRes.length > 0) {
                      const escapedName = (prize.name || '').replace(/'/g, "\\'");
                      await sql_query(`
                        INSERT INTO customer_history 
                        (mb_id, customer_idx, type, title, content, status, created_at) 
                        VALUES (
                          '${mbId}', 
                          ${custRes[0].idx}, 
                          'service', 
                          '룰렛 당첨 선물 수령', 
                          '${escapedName} 기프티콘이 발송되었습니다.',
                          'done',
                          NOW()
                        )
                      `);
                    }
                  } catch (histErr) {
                    console.error('customer_history 기록 실패:', histErr);
                  }
                }
              } else {
                console.error('기프티콘 발송 실패:', sendResult.message);
              }
            } catch (sendErr) {
              console.error('기프티콘 발송 오류:', sendErr);
            }
          }
        }

      } catch (e) {
        console.error('결과 저장 실패:', e);
      }
    }

    function closeResultModal() {
      showResultModal.value = false;
      if (remainingSpins.value <= 0) {
        linkData.value.status = 'completed';
      }
    }

    // function showToastMessage(msg, type = 'success') {
    //   toastMessage.value = msg;
    //   toastType.value = type;
    //   showToastMsg.value = true;
    //   setTimeout(() => showToastMsg.value = false, 3000);
    // }

    // 상품 이미지 로드 (기프티쇼 API)
    async function loadPrizeImages() {
      if (!getGiftShowGoodsDetail) return;
      
      const loadPromises = prizes.value
        .filter(p => p.goodsCode)
        .map(async (prize) => {
          try {
            const res = await getGiftShowGoodsDetail(prize.goodsCode);
            if (res?.goodsDetail) {
              prize.imageUrl = res.goodsDetail.goodsImgB || res.goodsDetail.goodsImgS || null;
              if (res.goodsDetail.goodsName) {
                prize.description = res.goodsDetail.goodsName;
              }
            }
          } catch (e) {
            console.error(`이미지 로드 실패 [${prize.goodsCode}]:`, e);
          }
        });

      await Promise.all(loadPromises);
    }

    onMounted(() => {

      // 최초 1회만 뷰포트 기반으로 사이즈 결정 (이후 절대 변경 안 함)
      const w = window.innerWidth;
      if (w >= 1024) wheelSize.value = 380;
      else if (w >= 768) wheelSize.value = 320;
      else wheelSize.value = Math.max(200, Math.min(280, w - 80));

      loadPrizeImages();
      if (isAgentMode) {
        fetchAgentData();
      } else {
        fetchLinkData();
      }
    });

    return {
      // 페이지 상태
      pageLoading, pageError, linkData, verified, isAgentMode,
      phone, canVerify, formatPhoneInput, handleVerify,
      winHistory,
      // 룰렛
      wheel, spinKey, wheelItems, wheelResultIndex, wheelSize,
      isSpinning, result, remainingSpins,
      showResultModal,
      showToastMsg, toastMessage, toastType,
      launchWheel, onWheelStart, onWheelEnd,
      closeResultModal,
      kakaoInvite
    };
  }
};
</script>

<style scoped>
* {
  -webkit-tap-highlight-color: transparent;
  box-sizing: border-box;

  /* 룰렛 페이지 전용 변수 */
  --roulette-card-bg: light-dark(#fff, #1e1e1e);
  --roulette-card-subtle: light-dark(#F5F5F5, #2a2a2a);
  --roulette-card-accent: light-dark(#FFF8E1, #332d1a);
  --roulette-border: light-dark(#e0e0e0, #444);
  --roulette-input-bg: light-dark(#fff, #1a1a1a);
  --roulette-input-focus-bg: light-dark(#FFF8E1, #332d1a);
  --roulette-disabled-bg: light-dark(#e0e0e0, #444);
  --roulette-winner-bg: light-dark(#FFF8E1, #2a2510);
}

/* ==========================================================
   모바일 퍼스트 기본 스타일 (< 768px)
   ========================================================== */

/* ========== 페이지 상태 (로딩/에러/만료/완료/인증) ========== */
.page-status {
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #FFD74A 0%, #FFA726 100%);
  padding: 16px;
}

.status-card {
  background: var(--roulette-card-bg);
  border-radius: 20px;
  padding: 32px 20px;
  text-align: center;
  width: 100%;
  max-width: 400px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.status-card i {
  font-size: 52px;
  margin-bottom: 12px;
}

.status-card .ph-spinner { color: #FF9800; }
.status-card .ph-x-circle { color: #f44336; }
.status-card .ph-clock-countdown { color: #FF9800; }
.status-card .ph-check-circle { color: #4CAF50; }

.spinning {
  animation: spin 1s linear infinite;
}

.status-card h2 {
  margin: 0 0 6px;
  font-size: 18px;
  font-weight: 800;
  color: var(--color-text-primary);
}

.status-card p {
  margin: 0 0 4px;
  font-size: 13px;
  color: var(--color-text-secondary);
}

/* 당첨 내역 */
.history-summary {
  margin-top: 16px;
  text-align: left;
}

.history-summary h4 {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 700;
  color: var(--color-text-primary);
}

.history-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: var(--roulette-card-accent);
  border-radius: 10px;
  margin-bottom: 6px;
}

.history-img {
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 6px;
}

.history-item span {
  font-size: 13px;
  font-weight: 700;
  color: var(--color-text-primary);
}

/* 본인 인증 화면 */
.status-card.verify {
  padding: 24px 20px;
}

.verify-header {
  margin-bottom: 20px;
}

.verify-emoji {
  font-size: 40px;
  display: block;
  margin-bottom: 10px;
}

.verify-header h2 {
  font-size: 20px;
  margin-bottom: 6px;
}

.verify-header p {
  font-size: 14px;
  color: var(--color-text-secondary);
}

.verify-header p strong {
  color: var(--color-text-primary);
}

.verify-chances {
  font-size: 15px !important;
  font-weight: 700 !important;
  color: #E65100 !important;
  margin-top: 6px !important;
}

.verify-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.verify-form label {
  font-size: 13px;
  color: var(--color-text-secondary);
}

.verify-form input {
  width: 100%;
  padding: 14px;
  border: 2px solid var(--roulette-border);
  border-radius: 12px;
  font-size: 16px;
  text-align: center;
  letter-spacing: 2px;
  font-weight: 500;
  background: var(--roulette-input-bg);
  color: var(--color-text-primary);
}

.verify-form input:focus {
  outline: none;
  border-color: #FF9800;
  background: var(--roulette-input-focus-bg);
}

.verify-btn {
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 12px;
  background: linear-gradient(135deg, #FFD74A 0%, #FFA726 100%);
  color: #333;
  font-size: 16px;
  font-weight: 800;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
}

.verify-btn:disabled {
  background: var(--roulette-disabled-bg);
  color: var(--color-text-secondary);
  cursor: not-allowed;
}


/* ========== 룰렛 메인 ========== */
.container {
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  background: #FFD74A;
  align-items: center;
  justify-content: center;
}

.roulette-container {
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  background-image: url('~@/../public/imgs/common/roulette_bg.png');
  background-position: center center;
  background-size: cover;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 16px;
  padding: 24px 16px;
  padding-bottom: max(24px, env(safe-area-inset-bottom));
}

.roulette-title {
  font-size: 1.8em;
  font-weight: 900;
  color: #fff;
  text-shadow: 0 4px 20px rgba(0,0,0,0.3);
  padding: 0;
  margin: 0;
  text-align: center;
}

.roulette-wrapper {
  width: min(280px, calc(100vw - 80px));
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex-shrink: 0;
}

.dial-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-position: center center;
  background-size: contain;
  background-repeat: no-repeat;
  transition: opacity 0.5s ease-in-out;
  z-index: 1;
}

.dial-a {
  background-image: url('~@/../public/imgs/common/roulette_dial_A.png');
  animation: dialFadeA 2s ease-in-out infinite;
}

.dial-b {
  background-image: url('~@/../public/imgs/common/roulette_dial_B.png');
}

@keyframes dialFadeA {
  0%, 45% { opacity: 1; }
  50%, 95% { opacity: 0; }
  100% { opacity: 1; }
}

.wheel-pointer {
  position: absolute;
  top: 2px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 20px;
  color: var(--color-text-primary);
  z-index: 20;
  filter: drop-shadow(0 2px 3px rgba(0,0,0,0.3));
}

:deep(.roulette-container) {
  font-family: inherit !important;
  position: relative;
  z-index: 2;
}

:deep(.roulette-container .wheel) {
  border: none !important;
  box-shadow: none !important;
}

:deep(.roulette-container .wheel .wheel-border) {
  display: none !important;
}

:deep(.roulette-container .indicator) {
  display: none !important;
}


/* ========== 하단 버튼 영역 ========== */
.extra {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: min(300px, calc(100vw - 48px));
  flex-direction: column;
  gap: 10px;
}

.chance-badge {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 100px;
  background: var(--roulette-card-bg);
  color: var(--color-text-primary);
  border: none;
  font-weight: 700;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
  width: 100%;
  height: 40px;
  font-size: 13px;
}

.spin-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 100px;
  background: var(--color-background-reverse);
  color: var(--color-background);
  border: none;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0,0,0,0.3);
  transition: all 0.2s;
  width: 100%;
  height: 46px;
  font-size: 15px;
  gap: 6px;
}

.spin-btn:active:not(.disabled) {
  transform: scale(0.96);
}

.spin-btn.disabled {
  background: var(--color-text-secondary);
  cursor: not-allowed;
}

.spin-btn b {
  color: var(--color-brand);
}


/* ========== 결과 모달 ========== */
.modal-overlay {
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  z-index: 1000;
  padding: 0;
}

.result-modal {
  background: var(--roulette-card-bg);
  border-radius: 24px 24px 0 0;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  box-shadow: 0 -8px 40px rgba(0,0,0,0.2);
  padding-bottom: env(safe-area-inset-bottom);
}

.result-modal.winner {
  background: linear-gradient(180deg, var(--roulette-winner-bg) 0%, var(--roulette-card-bg) 100%);
}

.result-content {
  padding: 28px 20px 20px;
  text-align: center;
}

.result-emoji {
  font-size: 48px;
  margin-bottom: 6px;
}

.result-title {
  margin: 0 0 16px;
  font-size: 22px;
  font-weight: 900;
  color: var(--color-text-primary);
}

.result-prize {
  margin-bottom: 16px;
}

.result-prize-icon {
  width: 68px;
  height: 68px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 34px;
  margin: 0 auto 10px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.prize-img {
  width: 44px;
  height: 44px;
  object-fit: contain;
  border-radius: 8px;
}

.result-prize-name {
  margin: 0 0 4px;
  font-size: 19px;
  font-weight: 900;
  color: var(--color-text-primary);
}

.result-prize-desc {
  margin: 0;
  font-size: 13px;
  color: var(--color-text-secondary);
  font-weight: 600;
}

.result-message {
  background: var(--roulette-card-subtle);
  border-radius: 12px;
  padding: 12px 14px;
}

.result-message p {
  margin: 0;
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.6;
  font-weight: 600;
}

.result-actions {
  padding: 16px 20px 20px;
  background: var(--roulette-card-subtle);
}

.result-btn {
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
}

.result-btn:active {
  transform: scale(0.96);
}

.result-btn.confirm {
  background: var(--color-background-reverse);
  color: var(--color-background);
}


/* ========== 토스트 ========== */
.toast {
  position: fixed;
  bottom: max(24px, env(safe-area-inset-bottom, 24px));
  left: 50%;
  transform: translateX(-50%);
  background: #333;
  color: #fff;
  padding: 12px 20px;
  border-radius: 50px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 700;
  font-size: 13px;
  box-shadow: 0 6px 20px rgba(0,0,0,0.3);
  z-index: 1100;
  max-width: calc(100% - 32px);
  white-space: nowrap;
}

.toast-icon { font-size: 16px; }
.toast.success .toast-icon { color: #4CAF50; }
.toast.info .toast-icon { color: #42A5F5; }


/* ==========================================================
   태블릿 (768px ~)
   ========================================================== */
@media (min-width: 768px) {
  .page-status {
    padding: 40px;
  }

  .status-card {
    border-radius: 24px;
    padding: 40px 30px;
  }

  .status-card i { font-size: 64px; }
  .status-card h2 { font-size: 20px; }
  .status-card p { font-size: 14px; }

  .status-card.verify { padding: 30px 24px; }
  .verify-emoji { font-size: 48px; }
  .verify-header h2 { font-size: 24px; }
  .verify-form input { padding: 16px; font-size: 17px; }
  .verify-btn { padding: 16px; font-size: 17px; }

  .container { padding: 40px; }

  .roulette-container {
    max-width: 600px;
    min-height: auto;
    aspect-ratio: 1 / 1.2;
    border-radius: 32px;
    gap: 32px;
    padding: 50px 40px;
    box-shadow: 0 20px 60px rgba(0,0,0,0.2);
  }

  .roulette-title { font-size: 3.5em; }

  .roulette-wrapper {
    width: 380px;
  }

  .wheel-pointer { top: 8px; font-size: 32px; }

  .extra {
    max-width: 400px;
    flex-direction: row;
    height: 56px;
    gap: 16px;
  }

  .chance-badge { width: auto; flex: 2; height: 100%; font-size: 16px; }
  .spin-btn { width: auto; flex: 4; height: 100%; font-size: 18px; gap: 10px; }

  /* 모달: 태블릿부터 중앙 팝업 */
  .modal-overlay {
    align-items: center;
    padding: 20px;
  }

  .result-modal {
    border-radius: 24px;
    max-width: 380px;
    padding-bottom: 0;
  }

  .result-content { padding: 32px 24px 24px; }
  .result-emoji { font-size: 56px; }
  .result-title { font-size: 26px; }
  .result-prize-icon { width: 80px; height: 80px; font-size: 40px; }
  .prize-img { width: 50px; height: 50px; }
  .result-prize-name { font-size: 22px; }
  .result-actions { padding: 20px 24px 24px; }
}


/* ==========================================================
   PC (1024px ~)
   ========================================================== */
@media (min-width: 1024px) {
  .container { padding: 0; }

  .roulette-container {
    max-width: 700px;
    gap: 40px;
    padding: 60px 50px;
  }

  .roulette-title { font-size: 4em; }

  .roulette-wrapper {
    width: 450px;
  }

  .wheel-pointer { top: 10px; font-size: 36px; }

  .extra { max-width: 450px; height: 60px; gap: 20px; }
  .chance-badge { font-size: 17px; }
  .spin-btn { font-size: 20px; }

  .spin-btn:hover:not(.disabled) {
    background: var(--color-text-primary);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0,0,0,0.4);
  }

  .verify-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 152, 0, 0.4);
  }
}


/* ========== 애니메이션 ========== */
.modal-enter-active, .modal-leave-active { transition: all 0.3s ease; }
.modal-enter-from, .modal-leave-to { opacity: 0; }
.modal-enter-from .result-modal, .modal-leave-to .result-modal {
  transform: translateY(100%);
}

@media (min-width: 768px) {
  .modal-enter-from .result-modal, .modal-leave-to .result-modal {
    transform: scale(0.9) translateY(20px);
  }
}

.toast-enter-active, .toast-leave-active { transition: all 0.3s ease; }
.toast-enter-from, .toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(20px);
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>